import * as React from 'react';

import { DescriptionPage_LearningProductType as ProductVariant } from '__generated__/graphql-types';

import { useCoursePageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';
import { useSpecializationPageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import type { XdpRecommendationsQueryData } from 'bundles/unified-description-page-common/hooks/useXdpRecommendationsQuery';
import useXdpRecommendationsQueryData from 'bundles/unified-description-page-common/hooks/useXdpRecommendationsQuery';
import { useXdpSubscriptionsForwardDiscovery } from 'bundles/unified-description-page-common/providers/SubscriptionsForwardDiscoveryDataProvider';

const XdpRecommendationsContext = React.createContext<XdpRecommendationsQueryData | undefined>(undefined);

type EligibleProductVariants = 'specialization' | 'course' | 'professional-certificate' | 'project';

type Props = {
  children: React.ReactNode;
  productVariant: EligibleProductVariants;
};

export const useXdpRecommendations = () => React.useContext(XdpRecommendationsContext);

const getProductVariant = (productVariant: EligibleProductVariants): ProductVariant => {
  switch (productVariant) {
    case 'specialization':
      return ProductVariant.Specialization;
    case 'professional-certificate':
      // Professional certificates are specializations under the hood,
      // so we use s21n here when fetching recommendations
      return ProductVariant.Specialization;
    case 'course':
      return ProductVariant.Course;
    case 'project':
      // Projects are courses under the hood,
      // so we use course here when fetching recommendations
      return ProductVariant.Course;
    default:
      return ProductVariant.Specialization;
  }
};

const RecommendationsDataProvider = ({ children, productVariant }: Props) => {
  const s12nPageData = useSpecializationPageData();
  const coursePageData = useCoursePageData();
  const pageData = s12nPageData ?? coursePageData;

  const { shouldShowXdpChanges } = useXdpSubscriptionsForwardDiscovery();
  const productVariantToUse = getProductVariant(productVariant);

  const data = useXdpRecommendationsQueryData({
    productVariant: productVariantToUse,
    productId: pageData?.id,
    showOnlyCplusEligibleRecs: shouldShowXdpChanges,
    skip: !shouldShowXdpChanges,
  });

  return <XdpRecommendationsContext.Provider value={data}>{children}</XdpRecommendationsContext.Provider>;
};

export default RecommendationsDataProvider;
